import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import cloudbase from "@cloudbase/js-sdk";

interface User {
  email: string;
  displayName: string;
  avatar?: string;
  memberType: 'normal' | 'vip';
  memberExpiry?: string;
  entitlements?: string[];
}

interface StoredAuth {
  user: User;
  expiresAt: number;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  showLoginDialog: boolean;
  setShowLoginDialog: (show: boolean) => void;
  showRegisterDialog: boolean;
  setShowRegisterDialog: (show: boolean) => void;
  register: (email: string, password: string) => Promise<boolean>;
  updateUser: (updates: Partial<User>) => void;
  getIdToken: () => Promise<string | null>;
  getUid: () => Promise<string | null>;
  sendEmailCode: (email: string) => Promise<boolean>;
  loginWithEmailCode: (email: string, code: string) => Promise<boolean>;
  refreshMembership: () => Promise<void>;
  sendRegisterEmailCode?: (email: string) => Promise<boolean>;
  registerWithEmailCode?: (email: string, code: string, password: string) => Promise<boolean>;
  checkEmailRegistered?: (email: string) => Promise<boolean>;
  lastAuthError: string | null;
  changePassword: (newPassword: string, emailCode?: string) => Promise<{ ok: boolean; message?: string }>;
  canChangePassword: () => boolean;
  sendPasswordEmailCode?: () => Promise<{ ok: boolean; message?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = "dadooooo_auth";
const USER_OVERRIDES_KEY = "dadooooo_profile_overrides";
const PASSWORD_CHANGE_LAST_SUCCESS_KEY = "dadooooo_pwd_change_last_success";
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
export const TCB_ENV_ID = "cloud1-5gngtt9b246815d4";
export const app = cloudbase.init({ env: TCB_ENV_ID, region: "ap-shanghai" });
const auth = app.auth({ persistence: "local" });
const db = app.database();
const ADMIN_WHITELIST = ["mjni2423@gmail.com", "caigg2434@gmail.com"];
const ADMIN_LOCK_URL = `https://${TCB_ENV_ID}-1371356488.tcloudbaseapp.com/admin/lock`;

async function getPublicIP(): Promise<string | null> {
  try {
    const r = await fetch('https://api.ipify.org?format=json');
    const j = await r.json();
    return typeof j?.ip === 'string' ? j.ip : null;
  } catch {
    return null;
  }
}

async function adminLockRequest(payload: { email: string; ip: string | null; action: 'check' | 'release' }) {
  const { email, ip, action } = payload;
  try {
    const sdkRes: any = await (app as any).callFunction?.({ name: 'adminLockGuard', data: { email, ip, action } });
    const r = sdkRes?.result;
    if (r && typeof r === 'object') {
      if (r.body) {
        try { return JSON.parse(r.body); } catch { return r; }
      }
      return r;
    }
  } catch {}
  try {
    const res = await fetch(ADMIN_LOCK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ip, action })
    });
    const j = await res.json().catch(() => ({}));
    return j;
  } catch {
    return { ok: true, skipped: true };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [lastAuthError, setLastAuthError] = useState<string | null>(null);
  const [emailVerificationInfo, setEmailVerificationInfo] = useState<any | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const tokRes: any = await (auth as any).getAccessToken?.();
        const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
        if (token) {
          let email = "";
          try {
            const info: any = await auth.getUserInfo?.();
            if (info && info.email) email = info.email;
          } catch {}
          const displayName = email ? email.split("@")[0] : "";
          let restoredUser: User = {
            email,
            displayName,
            memberType: ADMIN_WHITELIST.includes(email) ? "vip" : "normal",
            entitlements: []
          };
          try {
            const overridesRaw = localStorage.getItem(USER_OVERRIDES_KEY);
            if (overridesRaw) {
              const overrides = JSON.parse(overridesRaw);
              const ov = overrides[restoredUser.email];
              if (ov) {
                restoredUser = {
                  ...restoredUser,
                  displayName: ov.displayName ?? restoredUser.displayName,
                  avatar: ov.avatar ?? restoredUser.avatar,
                };
              }
            }
          } catch {}
          setUser(restoredUser);
          setIsLoggedIn(true);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isLoggedIn) return;
      try {
        const uid = await getUid();
        let rec: any | null = null;
        try {
          if (uid) {
            const res = await db.collection('users').where({ uid }).get();
            if (res && Array.isArray(res.data) && res.data.length > 0) {
              rec = res.data[0];
            }
          }
          if (!rec && user?.email) {
            const res2 = await db.collection('users').where({ email: user.email }).get();
            if (res2 && Array.isArray(res2.data) && res2.data.length > 0) {
              rec = res2.data[0];
            }
          }
        } catch {}

        if (!rec) {
          try {
            const r = await (app as any).callFunction?.({ name: 'createOrder', data: { action: 'getMembership', uid, email: user?.email } });
            const data = r?.result?.data;
            if (data) {
              const memberType = data.memberType || 'normal';
              const memberExpiry = data.vipUntil ? new Date(data.vipUntil).toISOString().slice(0, 10) : undefined;
              let ents: string[] = Array.isArray((data as any).entitlements) ? (data as any).entitlements : [];
              try {
                const qKey: any = uid ? { uid } : { email: user?.email };
                const a = await db.collection('authorizations').where({ ...qKey, status: 'active' }).get();
                const arr = Array.isArray(a?.data) ? a.data : [];
                const fromClient = arr.map((x: any) => String(x.product)).filter(Boolean);
                if (fromClient && fromClient.length) ents = fromClient;
              } catch {}
              updateUser({ memberType, memberExpiry, entitlements: ents });
              return;
            }
          } catch {}
        }
        if (rec) {
          const memberType = rec.memberType || 'normal';
          const memberExpiry = rec.vipUntil ? new Date(rec.vipUntil).toISOString().slice(0, 10) : undefined;
          let ents: string[] = [];
          try {
            const qKey: any = uid ? { uid } : { email: user?.email };
            const a = await db.collection('authorizations').where({ ...qKey, status: 'active' }).get();
            const arr = Array.isArray(a?.data) ? a.data : [];
            ents = arr.map((x: any) => String(x.product)).filter(Boolean);
          } catch {}
          if (!ents || ents.length === 0) {
            try {
              const r2 = await (app as any).callFunction?.({ name: 'createOrder', data: { action: 'getMembership', uid, email: user?.email } });
              const data2 = r2?.result?.data;
              if (Array.isArray((data2 as any)?.entitlements)) ents = (data2 as any).entitlements;
            } catch {}
          }
          updateUser({ memberType, memberExpiry, entitlements: ents });
        }
      } catch {}
    })();
  }, [isLoggedIn]);

  const saveAuthToStorage = (user: User) => {
    const expiresAt = Date.now() + ONE_MONTH_MS;
    const authData: StoredAuth = { user, expiresAt };
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    } catch {}
  };

  const login = async (email: string, password: string) => {
    try {
      if (ADMIN_WHITELIST.includes(email)) {
        const ip = await getPublicIP();
        if (ip) {
          const j = await adminLockRequest({ email, ip, action: 'check' });
          if (j && j.ok === false) {
            const msg = j?.code === 'LOCKED_BY_OTHER_IP'
              ? `该账号已在其他IP (${j?.currentIp || '未知'}) 登录，如需登录请先在其它设备退出。`
              : '管理员登录限制校验失败';
            setLastAuthError(msg);
            return false;
          }
        }
      }
      await auth.signIn({ username: email, password });
      const tokRes: any = await (auth as any).getAccessToken?.();
      const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
      if (!token) return false;
      const displayName = email.split("@")[0];
      let userData: User = { email, displayName, memberType: ADMIN_WHITELIST.includes(email) ? "vip" : "normal" };
      try {
        const overridesRaw = localStorage.getItem(USER_OVERRIDES_KEY);
        if (overridesRaw) {
          const overrides = JSON.parse(overridesRaw);
          const current = overrides[email];
          if (current) {
            userData = {
              ...userData,
              displayName: current.displayName ?? userData.displayName,
              avatar: current.avatar ?? userData.avatar,
            };
          }
        }
      } catch {}
      setUser(userData);
      setIsLoggedIn(true);
      setShowLoginDialog(false);
      saveAuthToStorage(userData);
      return true;
    } catch (e: any) {
      const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
      setLastAuthError(msg);
      return false;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      await auth.signUp({ email, password });
      const tokRes: any = await (auth as any).getAccessToken?.();
      const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
      if (!token) throw new Error('access token missing after signUp');
      const displayName = email.split("@")[0];
      const userData: User = { email, displayName, memberType: ADMIN_WHITELIST.includes(email) ? "vip" : "normal" };
      setUser(userData);
      setIsLoggedIn(true);
      setShowRegisterDialog(false);
      saveAuthToStorage(userData);
      return true;
    } catch (e: any) {
      const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
      setLastAuthError(msg);
      try {
        await auth.signIn({ username: email, password });
        const ok = await login(email, password);
        if (ok) {
          setShowRegisterDialog(false);
          return true;
        }
      } catch (ee: any) {
        const m2 = (ee?.message || ee?.code || ee?.error?.message || ee?.error?.code || JSON.stringify(ee));
        setLastAuthError(m2 || lastAuthError);
      }
      return false;
    }
  };

  const sendEmailCode = async (email: string) => {
    try {
      setLastAuthError(null);
      const info = await auth.getVerification({ email });
      setEmailVerificationInfo(info);
      return true;
    } catch (e: any) {
      const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
      setLastAuthError(msg || "验证码发送失败");
      return false;
    }
  };

  const sendRegisterEmailCode = async (email: string) => {
    return sendEmailCode(email);
  };

  const registerWithEmailCode = async (email: string, code: string, password: string) => {
    try {
      setLastAuthError(null);
      const info = emailVerificationInfo || await auth.getVerification({ email });
      setEmailVerificationInfo(info);
      const verifyRes = await auth.verify({ verification_id: info.verification_id, verification_code: code });
      await auth.signUp({ email, verification_code: code, verification_token: verifyRes.verification_token, password });
      const tokRes: any = await (auth as any).getAccessToken?.();
      const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
      if (!token) return false;
      const displayName = email.split("@")[0];
      const userData: User = { email, displayName, memberType: ADMIN_WHITELIST.includes(email) ? "vip" : "normal" };
      setUser(userData);
      setIsLoggedIn(true);
      setShowRegisterDialog(false);
      saveAuthToStorage(userData);
      return true;
    } catch (e: any) {
      const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
      setLastAuthError(msg || "邮箱验证码注册失败");
      return false;
    }
  };

  const checkEmailRegistered = async (email: string) => {
    try {
      const res = await db.collection('users').where({ email }).get();
      if (Array.isArray(res?.data) && res.data.length > 0) return true;
    } catch {
      
    }
    try {
      const info = await auth.getVerification({ email });
      return !!info?.is_user;
    } catch {
      return false;
    }
  };
  const loginWithEmailCode = async (email: string, code: string) => {
    try {
      setLastAuthError(null);
      if (ADMIN_WHITELIST.includes(email)) {
        const ip = await getPublicIP();
        if (ip) {
          const j = await adminLockRequest({ email, ip, action: 'check' });
          if (j && j.ok === false) {
            const msg = j?.code === 'LOCKED_BY_OTHER_IP'
              ? `该账号已在其他IP (${j?.currentIp || '未知'}) 登录，如需登录请先在其它设备退出。`
              : '管理员登录限制校验失败';
            setLastAuthError(msg);
            return false;
          }
        }
      }
      const info = emailVerificationInfo || await auth.getVerification({ email });
      setEmailVerificationInfo(info);
      const verifyRes = await auth.verify({ verification_id: info.verification_id, verification_code: code });
      if (info.is_user) {
        await auth.signIn({ username: email, verification_token: verifyRes.verification_token });
      } else {
        await auth.signUp({ email, verification_code: code, verification_token: verifyRes.verification_token });
      }
      const tokRes: any = await (auth as any).getAccessToken?.();
      const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
      if (!token) return false;
      const displayName = email.split("@")[0];
      let userData: User = { email, displayName, memberType: ADMIN_WHITELIST.includes(email) ? "vip" : "normal" };
      try {
        const overridesRaw = localStorage.getItem(USER_OVERRIDES_KEY);
        if (overridesRaw) {
          const overrides = JSON.parse(overridesRaw);
          const current = overrides[email];
          if (current) {
            userData = {
              ...userData,
              displayName: current.displayName ?? userData.displayName,
              avatar: current.avatar ?? userData.avatar,
            };
          }
        }
      } catch {}
      setUser(userData);
      setIsLoggedIn(true);
      setShowLoginDialog(false);
      saveAuthToStorage(userData);
      return true;
    } catch (e: any) {
      const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
      setLastAuthError(msg || "验证码登录失败");
      return false;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
    } catch {}
    try {
      if (user && ADMIN_WHITELIST.includes(user.email)) {
        const ip = await getPublicIP();
        await adminLockRequest({ email: user.email, ip, action: 'release' });
      }
    } catch {}
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      saveAuthToStorage(updatedUser);
      try {
        const overridesRaw = localStorage.getItem(USER_OVERRIDES_KEY);
        const overrides = overridesRaw ? JSON.parse(overridesRaw) : {};
        overrides[user.email] = {
          displayName: updatedUser.displayName,
          avatar: updatedUser.avatar,
        };
        localStorage.setItem(USER_OVERRIDES_KEY, JSON.stringify(overrides));
      } catch {}
    }
  };

  const getIdToken = async () => {
    try {
      const tokRes: any = await (auth as any).getAccessToken?.();
      const token = typeof tokRes === 'string' ? tokRes : (tokRes?.accessToken || tokRes?.access_token);
      return token || null;
    } catch {
      return null;
    }
  };

  const getUid = async () => {
    try {
      try {
        const info: any = await auth.getUserInfo?.();
        if (info && (info.uid || info.user_id)) return info.uid || info.user_id || null;
      } catch {}
      const tok = await getIdToken();
      if (!tok) return null;
      const parts = tok.split('.');
      if (parts.length < 2) return null;
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '==='.slice((b64.length + 3) % 4);
      const payload = JSON.parse(atob(padded));
      const uid = payload.uid || payload.sub || payload.openid || null;
      return uid || null;
    } catch {
      return null;
    }
  };

  const refreshMembership = async () => {
    if (!isLoggedIn) return;
    try {
      const uid = await getUid();
      let rec: any | null = null;
      try {
        if (uid) {
          const res = await db.collection('users').where({ uid }).get();
          if (res && Array.isArray(res.data) && res.data.length > 0) {
            rec = res.data[0];
          }
        }
        if (!rec && user?.email) {
          const res2 = await db.collection('users').where({ email: user.email }).get();
          if (res2 && Array.isArray(res2.data) && res2.data.length > 0) {
            rec = res2.data[0];
          }
        }
      } catch {}
      if (!rec) {
        try {
          const r = await (app as any).callFunction?.({ name: 'createOrder', data: { action: 'getMembership', uid, email: user?.email } });
          const data = r?.result?.data;
          if (data) {
            const memberType = data.memberType || 'normal';
            const memberExpiry = data.vipUntil ? new Date(data.vipUntil).toISOString().slice(0, 10) : undefined;
            let ents: string[] = Array.isArray((data as any).entitlements) ? (data as any).entitlements : [];
            try {
              const qKey: any = uid ? { uid } : { email: user?.email };
              const a = await db.collection('authorizations').where({ ...qKey, status: 'active' }).get();
              const arr = Array.isArray(a?.data) ? a.data : [];
              const fromClient = arr.map((x: any) => String(x.product)).filter(Boolean);
              if (fromClient && fromClient.length) ents = fromClient;
            } catch {}
            updateUser({ memberType, memberExpiry, entitlements: ents });
            return;
          }
        } catch {}
      }
      if (rec) {
        const memberType = rec.memberType || 'normal';
        const memberExpiry = rec.vipUntil ? new Date(rec.vipUntil).toISOString().slice(0, 10) : undefined;
        let ents: string[] = [];
        try {
          const qKey: any = uid ? { uid } : { email: user?.email };
          const a = await db.collection('authorizations').where({ ...qKey, status: 'active' }).get();
          const arr = Array.isArray(a?.data) ? a.data : [];
          ents = arr.map((x: any) => String(x.product)).filter(Boolean);
        } catch {}
        updateUser({ memberType, memberExpiry, entitlements: ents });
      }
    } catch {}
  };

  const canChangePassword = () => {
    const email = user?.email;
    if (!email) return false;
    try {
      const raw = localStorage.getItem(PASSWORD_CHANGE_LAST_SUCCESS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      const ts = obj[email];
      if (!ts) return true;
      return Date.now() - ts >= 24 * 60 * 60 * 1000;
    } catch {
      return true;
    }
  };

  const changePassword = async (newPassword: string, emailCode?: string): Promise<{ ok: boolean; message?: string }> => {
    try {
      if (!user?.email) return { ok: false, message: "请先登录" };
      if (!canChangePassword()) return { ok: false, message: "一天只能修改一次" };
      let done = false;
      try {
        const info = emailVerificationInfo || await auth.getVerification({ email: user.email });
        setEmailVerificationInfo(info);
        if (!emailCode || String(emailCode).trim().length !== 6) {
          return { ok: false, message: "请输入邮箱中的6位验证码" };
        }
        const verifyRes = await auth.verify({ verification_id: info.verification_id, verification_code: String(emailCode).trim() });
        await (auth as any).resetPassword({ email: user.email, new_password: newPassword, verification_token: verifyRes.verification_token });
        done = true;
      } catch (e: any) {
        const msg = (e?.message || e?.code || e?.error?.message || e?.error?.code || JSON.stringify(e));
        setLastAuthError(msg);
      }
      if (!done) {
        const msg = lastAuthError || "密码更改失败";
        return { ok: false, message: msg };
      }
      try {
        const uid = await getUid();
        const key: any = uid ? { uid } : { email: user.email };
        const col = db.collection('users');
        const found = await col.where(key).get();
        const payload: any = { hasPassword: true, updateTime: new Date() };
        if (found && Array.isArray(found.data) && found.data.length > 0) {
          await col.doc(found.data[0]._id).update(payload);
        } else {
          await col.add({ ...key, ...payload, memberType: 'normal' });
        }
      } catch {}
      try {
        const raw = localStorage.getItem(PASSWORD_CHANGE_LAST_SUCCESS_KEY);
        const obj = raw ? JSON.parse(raw) : {};
        obj[user.email] = Date.now();
        localStorage.setItem(PASSWORD_CHANGE_LAST_SUCCESS_KEY, JSON.stringify(obj));
      } catch {}
      return { ok: true };
    } catch (e: any) {
      console.error("changePassword error:", e);
      let msg = e?.message || e?.msg;
      if (!msg && e?.error) {
        msg = e.error.message || e.error.code;
      }
      if (!msg && e?.code) {
        msg = `Error: ${e.code}`;
      }
      if (!msg) {
        msg = JSON.stringify(e);
      }
      setLastAuthError(msg || "密码更改失败");
      return { ok: false, message: msg };
    }
  };

  const sendPasswordEmailCode = async () => {
    try {
      if (!user?.email) return { ok: false, message: "请先登录" };
      setLastAuthError(null);
      const info = await auth.getVerification({ email: user.email });
      setEmailVerificationInfo(info);
      return { ok: true };
    } catch (e: any) {
      console.error("sendPasswordEmailCode error:", e);
      let msg = e?.message || e?.msg;
      if (!msg && e?.error) {
        msg = e.error.message || e.error.code;
      }
      if (!msg && e?.code) {
        msg = `Error: ${e.code}`;
      }
      if (!msg) {
        msg = JSON.stringify(e);
      }
      
      // Translate common errors
      if (typeof msg === 'string') {
        if (msg.includes("limit") || msg.includes("quota") || msg.includes("frequency")) {
          msg = "发送过于频繁，请稍后再试";
        } else if (msg.includes("Network Error")) {
          msg = "网络连接失败，请检查网络设置";
        }
      }
      
      setLastAuthError(msg || "验证码发送失败");
      return { ok: false, message: msg || "验证码发送失败" };
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isLoggedIn,
        user,
        login, 
        logout, 
        showLoginDialog, 
        setShowLoginDialog,
        showRegisterDialog,
        setShowRegisterDialog,
        register,
        sendRegisterEmailCode,
        registerWithEmailCode,
        checkEmailRegistered,
        updateUser,
        getIdToken,
        getUid,
        sendEmailCode,
        loginWithEmailCode,
        refreshMembership,
        lastAuthError,
        changePassword,
        canChangePassword,
        sendPasswordEmailCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
